/* eslint-disable react-hooks/exhaustive-deps */
import { MenuItem, SubMenu } from "react-pro-sidebar";
import { ButtonAndDescription } from "../components/TSLandingPage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import {
  faArrowAltCircleLeft,
  faChartArea,
  faEnvelope,
  faHouse,
  faLocationPinLock,
  faSearch,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { useValidatedUserPayload } from "../app/hooks";
import { useEffect, useState } from "react";
import { useLazyGetPillsByWorkspaceQuery } from "../services/gallusTools";

export function distributeObjects(
  data: ButtonAndDescription[],
  amountOfRows: number,
): ButtonAndDescription[][] {
  // Sort data alphabetically by title
  const sortedData = data
    .slice()
    .sort((a, b) => a.title.localeCompare(b.title));

  // Create an array to hold the result arrays
  const result: ButtonAndDescription[][] = Array.from(
    { length: amountOfRows },
    () => [],
  );

  // Distribute objects into rows
  sortedData.forEach((item, index) => {
    // Determine which row (sub-array) to put this item in
    const rowIndex = index % amountOfRows;
    result[rowIndex].push(item);
  });

  return result;
}

interface landingPageObject {
  title: string;
  liveboardArray: ButtonAndDescription[];
}

function generateSubMenuItems(
  liveboardArray: ButtonAndDescription[],
  subDomain: string,
) {
  return liveboardArray.map((liveboard) => {
    return (
      <MenuItem>
        <Link
          to={`/${subDomain}/product?productURL=${liveboard.link}&fromMenu=true`}
        >
          <div className="sidebar-item">{liveboard.title}</div>
        </Link>
      </MenuItem>
    );
  });
}

interface GenerateSidebarProps {
  productTitle: string;
  subDomainURL: string;
  staticLandingPageObjects?: landingPageObject[];
  dynamicLandingPageObjectsCode?: orderedDynamicTileCodeInterface[];
  otherObjects?: { sidebarElement: JSX.Element; groupPermission?: string };
}

export function GenerateSidebar(props: GenerateSidebarProps) {
  const [subMenuArray, setSubMenuArray] = useState<JSX.Element[]>([]);
  const [dynamicSubMenuArray, setDynamicSubMenuArray] = useState<
    {
      element: JSX.Element;
      order: number;
    }[]
  >([]);
  const userSession = useValidatedUserPayload();
  const [hasViewingRights, setHasViewingRights] = useState<boolean>(false);
  const [getTilesQuery, getTilesQueryResult] =
    useLazyGetPillsByWorkspaceQuery();

  useEffect(() => {
    const isAllowedToAccessObject = async () => {
      if (props.otherObjects && userSession.cognitoGroups) {
        if (props.otherObjects.groupPermission) {
          setHasViewingRights(
            (userSession.cognitoGroups.includes(
              props.otherObjects.groupPermission,
            ) ||
              userSession.cognitoGroups.includes("Administrator")) ??
              false,
          );
        } else {
          setHasViewingRights(true);
        }
      } else {
        setHasViewingRights(false);
      }
    };

    const generateDyanmicItems = async () => {
      if (props.dynamicLandingPageObjectsCode) {
        props.dynamicLandingPageObjectsCode.forEach(
          async (dynamicTileCode, index) => {
            if (
              userSession &&
              userSession.cognitoGroups &&
              (!dynamicTileCode.onlyShowForCognitoGroups ||
                (dynamicTileCode.onlyShowForCognitoGroups &&
                  (hasCommonElements(
                    userSession.cognitoGroups,
                    dynamicTileCode.onlyShowForCognitoGroups,
                  ) ||
                    userSession.cognitoGroups.includes("Administrator"))))
            ) {
              try {
                const tiles = await getTilesQuery({
                  workspace: dynamicTileCode.dynamicCode.code,
                }).unwrap();
                var sortedButtonArray: ButtonAndDescription[] = [];
                if (tiles.length === 0 && dynamicTileCode.dynamicCode.backup) {
                  //Redundant check. Some firewalls block the call but it doesn't register it as a failed call.
                  sortedButtonArray = dynamicTileCode.dynamicCode.backup
                    .slice()
                    .sort((a, b) => a.title.localeCompare(b.title));
                } else {
                  sortedButtonArray = tiles
                    .slice()
                    .sort((a, b) => a.title.localeCompare(b.title));
                }
                setDynamicSubMenuArray((current) => [
                  ...current,
                  {
                    element: (
                      <SubMenu
                        {...subMenuDefaultProps}
                        title={dynamicTileCode.dynamicCode.businessName}
                        icon={
                          <FontAwesomeIcon
                            className="me-2"
                            icon={faChartArea}
                          />
                        }
                      >
                        {generateSubMenuItems(
                          sortedButtonArray,
                          props.subDomainURL,
                        )}
                      </SubMenu>
                    ),
                    order: dynamicTileCode.order,
                  },
                ]);
              } catch {
                if (dynamicTileCode.dynamicCode.backup) {
                  const sortedButtonArrayBackup: ButtonAndDescription[] =
                    dynamicTileCode.dynamicCode.backup
                      .slice()
                      .sort((a, b) => a.title.localeCompare(b.title));
                  setDynamicSubMenuArray((current) => [
                    ...current,
                    {
                      element: (
                        <SubMenu
                          {...subMenuDefaultProps}
                          title={dynamicTileCode.dynamicCode.businessName}
                          icon={
                            <FontAwesomeIcon
                              className="me-2"
                              icon={faChartArea}
                            />
                          }
                        >
                          {generateSubMenuItems(
                            sortedButtonArrayBackup,
                            props.subDomainURL,
                          )}
                        </SubMenu>
                      ),
                      order: dynamicTileCode.order,
                    },
                  ]);
                }
              }
            }
          },
        );
      }
    };

    isAllowedToAccessObject();
    generateDyanmicItems();
  }, [userSession]);

  let extraMenuObject: JSX.Element =
    hasViewingRights && props.otherObjects ? (
      props.otherObjects?.sidebarElement
    ) : (
      <></>
    );

  useEffect(() => {
    if (props.staticLandingPageObjects) {
      props.staticLandingPageObjects.forEach((landingPageObject) => {
        const sortedLiveboardArray: ButtonAndDescription[] =
          landingPageObject.liveboardArray
            .slice()
            .sort((a, b) => a.title.localeCompare(b.title));

        setSubMenuArray((current) => [
          ...current,
          <SubMenu
            {...subMenuDefaultProps}
            title={landingPageObject.title}
            icon={<FontAwesomeIcon className="me-2" icon={faChartArea} />}
          >
            {generateSubMenuItems(sortedLiveboardArray, props.subDomainURL)}
          </SubMenu>,
        ]);
      });
    }
  }, []);

  return (
    <>
      <SubMenu
        {...subMenuDefaultProps}
        title={props.productTitle}
        icon={<FontAwesomeIcon className="me-2" icon={faUser} />}
      >
        <MenuItem>
          <Link to={`/${props.subDomainURL}`}>
            <div className="sidebar-item">
              <FontAwesomeIcon className="me-2" icon={faHouse} />
              Home
            </div>
          </Link>
        </MenuItem>
        {extraMenuObject}
        <MenuItem>
          <Link to={`/${props.subDomainURL}/contactus`}>
            <div className="sidebar-item">
              <FontAwesomeIcon className="me-2" icon={faEnvelope} />
              Contact Us
            </div>
          </Link>
        </MenuItem>
        <MenuItem>
          <Link to={`/${props.subDomainURL}/signout`}>
            <div className="sidebar-item">
              <FontAwesomeIcon className="me-2" icon={faArrowAltCircleLeft} />
              Log Out
            </div>
          </Link>
        </MenuItem>
      </SubMenu>
      <SubMenu
        {...subMenuDefaultProps}
        title={"Search"}
        icon={<FontAwesomeIcon className="me-2" icon={faLocationPinLock} />}
      >
        <MenuItem>
          <Link
            to={`/${props.subDomainURL}/product?productURL=answer&fromMenu=true`}
          >
            <div className="sidebar-item">
              <FontAwesomeIcon className="me-2" icon={faSearch} />
              Search
            </div>
          </Link>
        </MenuItem>
      </SubMenu>
      {subMenuArray}
      {dynamicSubMenuArray
        .sort((a, b) => a.order - b.order)
        .map((submenu) => {
          return submenu.element;
        })}
    </>
  );
}

export function populateNonEditableArray(
  collectionOfArraysOfButtons: ButtonAndDescription[][],
) {
  const nonEditableLiveboardsArray: string[] = [];
  collectionOfArraysOfButtons.forEach((arrayOfButtons) => {
    arrayOfButtons.forEach((individualLiveboard) => {
      if (individualLiveboard.link) {
        nonEditableLiveboardsArray.push(individualLiveboard.link.slice(9));
      }
    });
  });
  return nonEditableLiveboardsArray;
}

export const subMenuDefaultProps = {
  onPointerEnterCapture: () => {},
  onPointerLeaveCapture: () => {},
  placeholder: "",
};

export interface SubdomainOrgIdObject {
  orgID: number;
  path: string;
  externalInfo?: { subdomain: string; productURL: string };
  editableRightsGroup?: string;
}

export interface dynamicTileCodeInterface {
  code: string;
  businessName: string;
  backup?: ButtonAndDescription[];
}

export interface orderedDynamicTileCodeInterface {
  dynamicCode: dynamicTileCodeInterface;
  order: number;
  onlyShowForCognitoGroups?: string[];
}

export function hasCommonElements(arr1: string[], arr2: string[]): boolean {
  return arr1.some((element) => arr2.includes(element));
}
